import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/img/logo.png";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on page navigation
  useEffect(() => {
    setMenuOpen(false);
  }, [loc.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/features", label: "Features" },
    { to: "/about", label: "About" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <>
      <style>{`
        .wb-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 999;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 48px;
          background: rgba(8, 12, 20, ${scrolled ? "0.95" : "0.75"});
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border-bottom: 1px solid ${scrolled ? "rgba(0, 245, 196, 0.15)" : "rgba(255, 255, 255, 0.05)"};
          transition: all 0.3s ease;
          width: 100%;
        }

        .wb-logo {
          font-family: 'Orbitron', monospace;
          font-size: 20px;
          font-weight: 900;
          color: var(--neon);
          text-decoration: none;
          letter-spacing: 1.5px;
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
        }

        .wb-logo-img {
          height: 38px;
          width: auto;
          object-fit: contain;
        }

        .wb-logo span {
          color: var(--neon2);
        }

        .wb-nav-links {
          display: flex;
          gap: 28px;
          list-style: none;
          align-items: center;
          margin: 0;
          padding: 0;
        }

        .wb-nav-links a {
          color: var(--muted);
          text-decoration: none;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          transition: color 0.2s ease;
          position: relative;
          font-family: 'Rajdhani', sans-serif;
          padding: 6px 0;
        }

        .wb-nav-links a::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: var(--neon);
          transition: width 0.25s ease;
        }

        .wb-nav-links a:hover,
        .wb-nav-links a.active {
          color: var(--neon);
        }

        .wb-nav-links a:hover::after,
        .wb-nav-links a.active::after {
          width: 100%;
        }

        .wb-nav-cta {
          background: rgba(0, 245, 196, 0.08);
          border: 1.5px solid var(--neon);
          color: var(--neon);
          padding: 8px 20px;
          font-family: 'Orbitron', monospace;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.5px;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.25s ease;
          text-transform: uppercase;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }

        .wb-nav-cta:hover {
          background: var(--neon);
          color: var(--bg);
          box-shadow: 0 0 20px rgba(0, 245, 196, 0.4);
        }

        .wb-hamburger {
          display: none;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 5px;
          cursor: pointer;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid var(--border);
          border-radius: 8px;
          width: 42px;
          height: 42px;
          padding: 8px;
          transition: all 0.2s ease;
        }

        .wb-hamburger:hover {
          border-color: var(--neon);
          background: rgba(0, 245, 196, 0.08);
        }

        .wb-hamburger span {
          display: block;
          width: 22px;
          height: 2px;
          background: var(--neon);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 2px;
        }

        /* Tablet Responsive (769px - 1024px) */
        @media (max-width: 1024px) and (min-width: 769px) {
          .wb-header {
            padding: 16px 28px;
          }
          .wb-nav-links {
            gap: 18px;
          }
          .wb-nav-links a {
            font-size: 12px;
            letter-spacing: 1px;
          }
          .wb-nav-cta {
            padding: 7px 16px;
            font-size: 10px;
          }
        }

        /* Mobile Responsive (< 768px) */
        @media (max-width: 768px) {
          .wb-header {
            padding: 14px 20px;
          }
          .wb-logo {
            font-size: 18px;
          }
          .wb-logo-img {
            height: 32px;
          }
          .wb-nav-links,
          .wb-nav-cta {
            display: none;
          }
          .wb-hamburger {
            display: flex;
          }
        }

        /* Mobile Drawer Menu */
        .wb-mobile-drawer {
          position: fixed;
          top: 65px;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(8, 12, 20, 0.98);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          z-index: 998;
          display: flex;
          flex-direction: column;
          padding: 24px 24px 40px;
          overflow-y: auto;
          border-top: 1px solid var(--border);
          transform: translateY(${menuOpen ? "0" : "-100%"});
          opacity: ${menuOpen ? "1" : "0"};
          pointer-events: ${menuOpen ? "all" : "none"};
          transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease;
        }

        .wb-mobile-link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: var(--text);
          text-decoration: none;
          font-size: 16px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          padding: 16px 12px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          transition: all 0.2s ease;
          border-radius: 6px;
        }

        .wb-mobile-link.active,
        .wb-mobile-link:hover {
          color: var(--neon);
          background: rgba(0, 245, 196, 0.06);
          padding-left: 18px;
        }

        .wb-mobile-cta {
          margin-top: 24px;
          background: var(--neon);
          color: var(--bg);
          padding: 16px;
          font-family: 'Orbitron', monospace;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 2px;
          text-align: center;
          border-radius: 8px;
          text-transform: uppercase;
          text-decoration: none;
          display: block;
          box-shadow: 0 0 24px rgba(0, 245, 196, 0.35);
        }
      `}</style>

      <nav className="wb-header">
        <Link to="/" className="wb-logo">
          <img src={logo} alt="WalkBattle Logo" className="wb-logo-img" />
          Walk<span>Battle</span>
        </Link>

        <ul className="wb-nav-links">
          {navLinks.map(({ to, label }) => (
            <li key={to}>
              <Link to={to} className={loc.pathname === to ? "active" : ""}>
                {label}
              </Link>
            </li>
          ))}
        </ul>

        <Link
          to="https://play.google.com/store/apps/details?id=com.bluespace.walkbattle"
          target="_blank"
          rel="noopener noreferrer"
          className="wb-nav-cta"
        >
          ▶ Get App
        </Link>

        <button
          className="wb-hamburger"
          aria-label="Toggle navigation menu"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span
            style={{
              transform: menuOpen ? "rotate(45deg) translate(5px, 5px)" : "none",
            }}
          />
          <span style={{ opacity: menuOpen ? 0 : 1 }} />
          <span
            style={{
              transform: menuOpen ? "rotate(-45deg) translate(5px, -5px)" : "none",
            }}
          />
        </button>
      </nav>

      {/* Mobile Drawer Menu */}
      <div className="wb-mobile-drawer">
        {navLinks.map(({ to, label }) => (
          <Link
            key={to}
            to={to}
            className={`wb-mobile-link ${loc.pathname === to ? "active" : ""}`}
            onClick={() => setMenuOpen(false)}
          >
            <span>{label}</span>
            <span style={{ color: "var(--muted)", fontSize: 18 }}>›</span>
          </Link>
        ))}

        <Link
          to="https://play.google.com/store/apps/details?id=com.bluespace.walkbattle"
          target="_blank"
          rel="noopener noreferrer"
          className="wb-mobile-cta"
          onClick={() => setMenuOpen(false)}
        >
          ▶ Get App on Google Play
        </Link>
      </div>
    </>
  );
}

